/**
 * The Derived mark an export carries (UI-02, rule R4).
 *
 * The grid tags a derived LTV, DSCR or debt yield "calc" in its rendered cell
 * only. CSV, Excel, the Studio preview and the view grid the server prints
 * read each column's plain `display` string, so a ratio the platform computed
 * exported exactly like one a document stated. Every text export now appends
 * the mark to a derived figure and prints one legend; a numeric Excel cell
 * keeps its number and carries the basis as a cell note.
 *
 * The server's view grid marks the same columns the same way
 * (backend/app/reporting/view_grid.py `DERIVED_MARK`), so a scheduled report
 * and the toolbar's export read alike.
 */
import type { Loan } from '@/types'
import type { ColumnDef } from './columnTypes'

// The mark and the legend wording are unratified (4 Nov 2026) — Edward to sign off.
// They mirror view_grid.DERIVED_MARK / DERIVED_LEGEND on the server.
export const DERIVED_MARK = '(derived)'
export const DERIVED_LEGEND =
  '(derived) — computed by the platform from the deal’s other figures; no document states it.'

const MISSING = '—'

/** The reason a column's figure is derived for this loan, or null. */
export function derivedExportBasis(def: ColumnDef, loan: Loan): string | null {
  const basis = def.basis?.(loan) ?? null
  return basis && basis.trim() ? basis : null
}

/** The column's display text with the Derived mark when the figure is derived;
 *  a missing figure keeps its dash. For the on-screen report preview. */
export function markedDisplay(def: ColumnDef, loan: Loan): string {
  const text = def.display(loan)
  if (!text || text === MISSING) return text
  const basis = derivedExportBasis(def, loan)
  if (!basis) return text
  const mark = def.exportMark?.(loan) ?? DERIVED_MARK
  return mark ? `${text} ${mark}` : text
}

/** The cell as CSV, the view grid and an Excel text cell carry it: the marked
 *  display, with the em dash for a missing figure exported blank. */
export function exportCell(def: ColumnDef, loan: Loan): string {
  const text = markedDisplay(def, loan)
  return text === MISSING ? '' : text
}

/** Whether any cell of these columns over these loans carries the mark. */
export function anyDerived(defs: ColumnDef[], loans: Loan[]): boolean {
  return loans.some((loan) => defs.some((def) => derivedExportBasis(def, loan) != null))
}
